import { Injectable } from '@angular/core';
import {
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
} from 'docx';

const BODY_FONT = 'Tinos';
const SIGNATURE_LINE_PLACEHOLDER = '@@SIGNATURE_LINE@@';
const SIGNATURE_LINE_TEXT = '___________________________';

@Injectable({ providedIn: 'root' })
export class TemplateRendererService {
  render(template: string, _data?: Record<string, unknown>): string {
    if (!template) {
      return '';
    }

    return template.replace(
      new RegExp(SIGNATURE_LINE_PLACEHOLDER, 'g'),
      SIGNATURE_LINE_TEXT,
    );
  }

  async buildDocxFromMarkdown(markdown: string): Promise<Blob> {
    if (!markdown) {
      markdown = '';
    }

    const sanitized = this.render(markdown);
    const paragraphs: Paragraph[] = [];
    const lines = sanitized.split(/\r?\n/);

    let prevWasBullet = false;
    let blankAfterBulletCount = 0;

    for (let i = 0; i < lines.length; i++) {
      const rawLine = lines[i];
      const normalized = rawLine.replace(/\s/g, '');
      const line = rawLine.trim();

      // Horizontal rule
      if (/^(-{3,}|\*{3,}|_{3,})$/.test(normalized)) {
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: '_____________________________________________________________________________________________________________________________________________________________________________________________________________________________',
                size: 8,
                color: '000000',
                font: BODY_FONT,
              }),
            ],
            spacing: { before: 500, after: 300 },
          }),
        );
        prevWasBullet = false;
        blankAfterBulletCount = 0;
        continue;
      }

      // 🔍 Blank line handling
      if (!line) {
        if (prevWasBullet) {
          // 1) Check if this blank is BETWEEN bullets → skip it.
          let j = i + 1;
          let nextNonEmpty = '';

          while (j < lines.length) {
            nextNonEmpty = lines[j].trim();
            if (nextNonEmpty) break;
            j++;
          }

          if (nextNonEmpty && /^[-*]\s+/.test(nextNonEmpty)) {
            // It's between two bullets → don't output a blank paragraph.
            continue;
          }

          // 2) It's AFTER a bullet list (next non-empty is not a bullet).
          // Allow only ONE blank paragraph after the list.
          if (blankAfterBulletCount >= 1) {
            continue; // compress multiple blanks to one
          }

          blankAfterBulletCount++;
        }

        // For non-bullet context, or the first blank after a bullet list,
        // keep the blank as a real paragraph (this preserves spacing elsewhere).
        paragraphs.push(
          new Paragraph({
            alignment: AlignmentType.LEFT,
            children: this.createRunsFromInlineMarkdown('', {
              size: 24,
            }),
            spacing: { before: 40, after: 40 },
          }),
        );
        continue;
      }

      // #### Heading levels
      if (line.startsWith('#### ')) {
        const text = line.replace(/^####\s+/, '');
        paragraphs.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_4,
            alignment: AlignmentType.CENTER,
            children: this.createRunsFromInlineMarkdown(text, {
              italics: true,
              size: 24,
            }),
            spacing: { before: 0, after: 0 },
          }),
        );
        prevWasBullet = false;
        blankAfterBulletCount = 0;
        continue;
      }

      if (line.startsWith('### ')) {
        const text = line.replace(/^###\s+/, '');
        paragraphs.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_3,
            children: this.createRunsFromInlineMarkdown(text, {
              bold: true,
              size: 26,
            }),
            spacing: { before: 0, after: 0 },
          }),
        );
        prevWasBullet = false;
        blankAfterBulletCount = 0;
        continue;
      }

      if (line.startsWith('## ')) {
        const text = line.replace(/^##\s+/, '');
        paragraphs.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_2,
            alignment: AlignmentType.CENTER,
            children: this.createRunsFromInlineMarkdown(text, {
              bold: true,
              size: 30,
            }),
            spacing: { before: 0, after: 0 },
          }),
        );
        prevWasBullet = false;
        blankAfterBulletCount = 0;
        continue;
      }

      if (line.startsWith('# ')) {
        const text = line.replace(/^#\s+/, '');
        paragraphs.push(
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            children: this.createRunsFromInlineMarkdown(text, {
              bold: true,
              size: 36,
            }),
            spacing: { before: 40, after: 40 },
          }),
        );
        prevWasBullet = false;
        blankAfterBulletCount = 0;
        continue;
      }

      // 🔸 Bullet lines
      if (/^[-*]\s+/.test(line)) {
        const text = line.replace(/^[-*]\s+/, '');
        paragraphs.push(
          new Paragraph({
            bullet: { level: 0 },
            children: this.createRunsFromInlineMarkdown(text, {
              size: 24,
            }),
            spacing: { before: 40, after: 40 },
          }),
        );
        prevWasBullet = true;
        blankAfterBulletCount = 0;
        continue;
      }

      // 🔸 Normal paragraph
      paragraphs.push(
        new Paragraph({
          alignment: AlignmentType.LEFT,
          children: this.createRunsFromInlineMarkdown(line, {
            size: 24,
          }),
          spacing: { before: 40, after: 40 },
        }),
      );
      prevWasBullet = false;
      blankAfterBulletCount = 0;
    }

    const doc = new Document({
      sections: [{ properties: {}, children: paragraphs }],
    });

    return Packer.toBlob(doc);
  }

  private createRunsFromInlineMarkdown(
    text: string,
    base?: { bold?: boolean; italics?: boolean; size?: number },
  ): TextRun[] {
    const runs: TextRun[] = [];
    const pattern = /(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        runs.push(
          new TextRun({
            text: text.slice(lastIndex, match.index),
            bold: base?.bold,
            italics: base?.italics,
            size: base?.size,
            color: '000000',
            font: BODY_FONT,
          }),
        );
      }

      const token = match[0];
      let content = '';
      let bold = false;
      let italics = false;

      if (
        (token.startsWith('**') && token.endsWith('**')) ||
        (token.startsWith('__') && token.endsWith('__'))
      ) {
        content = token.slice(2, -2);
        bold = true;
      } else if (
        (token.startsWith('*') && token.endsWith('*')) ||
        (token.startsWith('_') && token.endsWith('_'))
      ) {
        content = token.slice(1, -1);
        italics = true;
      } else {
        content = token;
      }

      runs.push(
        new TextRun({
          text: content,
          bold: bold || base?.bold,
          italics: italics || base?.italics,
          size: base?.size,
          color: '000000',
          font: BODY_FONT,
        }),
      );

      lastIndex = pattern.lastIndex;
    }

    if (lastIndex < text.length) {
      runs.push(
        new TextRun({
          text: text.slice(lastIndex),
          bold: base?.bold,
          italics: base?.italics,
          size: base?.size,
          color: '000000',
          font: BODY_FONT,
        }),
      );
    }

    if (!runs.length) {
      runs.push(
        new TextRun({
          text,
          bold: base?.bold,
          italics: base?.italics,
          size: base?.size,
          color: '000000',
          font: BODY_FONT,
        }),
      );
    }

    return runs;
  }
}
